import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ImagePlus, Briefcase, X, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fileToImageDataUrl } from "@/lib/hooks";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";
import { BUSINESS_CATEGORIES } from "@/lib/businessCategories";

const CATEGORIES = BUSINESS_CATEGORIES.map((c) => c.label);

const UpgradeBusiness = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const fileRef = useRef(null);
    const [logo, setLogo] = useState(null);
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [categoryCustom, setCategoryCustom] = useState("");
    const [address, setAddress] = useState("");
    const [location, setLocation] = useState("");
    const [phone, setPhone] = useState(user?.phone || "");
    const [email, setEmail] = useState(user?.email || "");
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});

    const handleLogo = async (file) => {
        if (!file || !file.type?.startsWith("image/")) return;
        setBusy(true);
        try {
            const dataUrl = await fileToImageDataUrl(file, 800, 0.8);
            setLogo(dataUrl);
        } finally {
            setBusy(false);
        }
    };

    const submit = () => {
        const e = {};
        if (!name.trim()) e.name = "Business name is required";
        if (!category) e.category = "Pick a category";
        else if (category === "Other (specify)" && !categoryCustom.trim())
            e.categoryCustom = "Specify your business category";
        if (!address.trim()) e.address = "Business address is required";
        if (!location.trim()) e.location = "City / location is required";
        if (!phone.trim() && !email.trim()) e.contact = "Add a phone number or email";
        if (!description.trim() || description.trim().length < 20)
            e.description = "Tell clients a bit about you (20+ characters)";

        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        const businessProfile = {
            id: user.id,
            userId: user.id,
            businessName: name.trim(),
            businessCategory: category,
            businessCategoryCustom:
                category === "Other (specify)" ? categoryCustom.trim() : null,
            businessAddress: address.trim(),
            location: location.trim(),
            businessPhone: phone.trim() || null,
            businessEmail: email.trim() || null,
            businessDescription: description.trim(),
            businessLogo: logo || null,
            verified: false,
            verificationStatus: "pending",
            createdAt: new Date().toISOString(),
        };

        // Phase 3D: do NOT immediately flip the user to a business account.
        // Stash the pending profile fields under `pendingBusinessProfile`
        // so admin can review them before approval. accountType stays personal.
        updateUser({
            // Keep the profile fields written so the user can resubmit without
            // retyping if rejected — but mirror them under "pending" + flag.
            upgradeStatus: "pending",
            pendingBusinessProfile: businessProfile,
            // Persist business inputs on the user record (they're harmless on
            // a personal account; the UI only renders them once accountType
            // actually flips to "business" after admin approval).
            businessName: businessProfile.businessName,
            businessCategory: businessProfile.businessCategory,
            businessCategoryCustom: businessProfile.businessCategoryCustom || null,
            businessAddress: businessProfile.businessAddress,
            businessPhone: businessProfile.businessPhone,
            businessEmail: businessProfile.businessEmail,
            businessDescription: businessProfile.businessDescription,
            businessLogo: businessProfile.businessLogo,
            // location IS still the user's location even today — fine to set.
            location: businessProfile.location,
            verified: false,
            verificationStatus: "pending",
        });

        try { mirror(SUPABASE_TABLES.BUSINESS_PROFILES, businessProfile); } catch {}

        navigate("/verification/business?upgraded=1", { replace: true });
    };

    return (
        <div className="screen relative" data-testid="upgrade-business-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="upgrade-business-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">Upgrade to Business</h1>
                <span className="h-10 w-10" />
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-5">
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
                    <span className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                        <Briefcase size={18} />
                    </span>
                    <div>
                        <div className="font-display text-[14px] font-semibold">Sell on Neyra as a business</div>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                            List services, host events, accept bookings, and unlock the business
                            dashboard. Verification is required after upgrade.
                        </p>
                    </div>
                </div>

                {/* Logo */}
                <section data-testid="upgrade-business-logo-section">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-2 ml-1">
                        Business logo (optional)
                    </span>
                    <div className="flex items-center gap-3">
                        <div className="h-20 w-20 rounded-2xl overflow-hidden border border-border bg-secondary flex items-center justify-center relative">
                            {logo ? (
                                <>
                                    <img src={logo} alt="" className="h-full w-full object-cover" />
                                    <button
                                        type="button"
                                        data-testid="upgrade-business-logo-remove"
                                        onClick={() => setLogo(null)}
                                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border flex items-center justify-center text-destructive"
                                    >
                                        <X size={12} />
                                    </button>
                                </>
                            ) : (
                                <Briefcase size={22} className="text-muted-foreground" />
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="upgrade-business-logo-pick"
                            onClick={() => fileRef.current?.click()}
                            disabled={busy}
                            className="h-11 px-4 rounded-full bg-secondary text-secondary-foreground hover:bg-muted text-[13px] font-medium inline-flex items-center gap-1.5"
                        >
                            <ImagePlus size={14} />
                            {logo ? "Replace" : "Upload"}
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleLogo(e.target.files?.[0])}
                            data-testid="upgrade-business-logo-input"
                        />
                    </div>
                </section>

                <Field label="Business name" error={errors.name} testId="upgrade-business-name">
                    <input
                        data-testid="upgrade-business-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Glow Studio"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                <div data-testid="upgrade-business-category-field">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Business category
                    </span>
                    <div className="-mx-1 px-1 overflow-x-auto no-scrollbar">
                        <div className="flex flex-wrap gap-1.5">
                            {CATEGORIES.map((c) => {
                                const active = category === c;
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        data-testid={`upgrade-business-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        onClick={() => { setCategory(c); if (errors.category) setErrors({ ...errors, category: null }); }}
                                        className={[
                                            "h-9 px-3.5 rounded-full text-[12.5px] font-medium border transition-colors",
                                            active
                                                ? "bg-primary text-primary-foreground border-primary"
                                                : "bg-secondary text-foreground border-border hover:bg-muted",
                                        ].join(" ")}
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {errors.category && (
                        <p data-testid="upgrade-business-category-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.category}
                        </p>
                    )}
                    {category === "Other (specify)" && (
                        <div className="mt-2" data-testid="upgrade-business-category-custom-field">
                            <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                Specify your business category
                            </span>
                            <input
                                data-testid="upgrade-business-category-custom"
                                value={categoryCustom}
                                onChange={(e) => {
                                    setCategoryCustom(e.target.value);
                                    if (errors.categoryCustom) setErrors({ ...errors, categoryCustom: null });
                                }}
                                placeholder="Enter your category"
                                className={[
                                    "w-full h-12 px-3 rounded-xl border text-[14px] bg-secondary outline-none transition-colors",
                                    errors.categoryCustom
                                        ? "border-destructive bg-destructive/5"
                                        : "border-border focus:bg-background focus:border-primary",
                                ].join(" ")}
                            />
                            {errors.categoryCustom && (
                                <p data-testid="upgrade-business-category-custom-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                                    {errors.categoryCustom}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <Field label="Business address" error={errors.address} testId="upgrade-business-address">
                    <input
                        data-testid="upgrade-business-address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Street, building number, area"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                <Field label="City / Location" error={errors.location} testId="upgrade-business-location">
                    <input
                        data-testid="upgrade-business-location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. Lagos, Nigeria"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                <Field label="Business phone" testId="upgrade-business-phone">
                    <input
                        data-testid="upgrade-business-phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        inputMode="tel"
                        placeholder="+234…"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                <Field label="Business email" testId="upgrade-business-email">
                    <input
                        data-testid="upgrade-business-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="hello@yourbiz.com"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>
                {errors.contact && (
                    <p data-testid="upgrade-business-contact-error" className="text-[12px] text-destructive -mt-3 ml-1">
                        {errors.contact}
                    </p>
                )}

                <div>
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Business description
                    </span>
                    <textarea
                        data-testid="upgrade-business-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={4}
                        maxLength={500}
                        placeholder="What does your business do? What makes it special?"
                        className={[
                            "w-full p-3.5 rounded-2xl border outline-none text-[14px] resize-none transition-colors",
                            errors.description
                                ? "border-destructive bg-destructive/5"
                                : "border-border bg-secondary focus:bg-background focus:border-primary",
                        ].join(" ")}
                    />
                    {errors.description && (
                        <p data-testid="upgrade-business-description-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.description}
                        </p>
                    )}
                </div>

                <p className="ml-1 text-[11.5px] text-muted-foreground inline-flex items-start gap-1.5" data-testid="upgrade-business-verify-hint">
                    <ShieldCheck size={12} className="mt-0.5 text-primary shrink-0" />
                    <span>You'll be asked to upload your CAC / certificate and owner ID for verification after upgrade.</span>
                </p>
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background">
                <button
                    data-testid="upgrade-business-submit"
                    onClick={submit}
                    disabled={busy}
                    className="w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] disabled:opacity-60"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    Upgrade & continue to verification
                </button>
            </div>
        </div>
    );
};

const Field = ({ label, error, testId, children }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        <span
            className={[
                "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
                error
                    ? "border-destructive bg-destructive/5"
                    : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
            ].join(" ")}
        >
            {children}
        </span>
        {error && (
            <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>
                {error}
            </span>
        )}
    </label>
);

export default UpgradeBusiness;
